import React, { useRef, useState, useEffect, useCallback } from "react";
import { Modal, Flex, Button, Slider, Typography, Input, Select, Tooltip } from "antd";
import Draggable, { DraggableEvent, DraggableData } from "react-draggable";
import {
  PlayIcon,
  Sparkles,
  PauseIcon,
  GripHorizontal,
  StepForward,
  StepBack,
  Timer,
  RotateCcw,
  Snail,
  Rabbit,
  ArrowUp,
  X,
} from "lucide-react";
import "./styles.scss";
import { useAPI } from "../../hooks/useAPI";
import { generateTeleprompterContent } from "../../services/teleprompter";
import { useFeatureAccess } from "../../hooks/useFeatureAccess";
import { UpgradePrompt } from "../overlays/UpgradePrompt";

const { Paragraph } = Typography;
interface TeleprompterModalProps {
  open: boolean;
  fullpage?: boolean;
  text?: string;
  speed?: number;
  isRecording?: boolean;
  onClose: () => void;
  onTeleprompterStateChange?: (state: {
    text: string;
    currentWordIndex: number;
    isPlaying: boolean;
    highlightSpeed: number;
  }) => void;
  onScriptGenerated?: (script: string) => void;
  currentSceneScript?: string;
}

const TeleprompterModal: React.FC<TeleprompterModalProps> = ({
  open,
  fullpage,
  text,
  speed,
  isRecording = false,
  onClose,
  onTeleprompterStateChange,
  onScriptGenerated,
  currentSceneScript,
}) => {
  const [bounds, setBounds] = useState({
    left: 0,
    top: 0,
    bottom: 0,
    right: 0,
  });
  const draggleRef = useRef<HTMLDivElement>(null);

  const [teleprompterText, setTeleprompterText] = useState("");

  const [highlightSpeed, setHighlightSpeed] = useState(500);

  const defaultTeleprompterText = "Your teleprompter script goes here.";

  const validateAndSetTeleprompterText = (value: string) => {
    // Check if value is empty, only whitespace, or only empty lines
    const trimmedValue = value.trim();
    if (!trimmedValue) {
      // Reset to default placeholder
      setTeleprompterText(defaultTeleprompterText);
      localStorage.setItem("teleprompter_text", defaultTeleprompterText);
      syncTeleprompterState({
        text: defaultTeleprompterText,
      });
    } else {
      // Set the valid text
      setTeleprompterText(value);
      localStorage.setItem("teleprompter_text", value);
      syncTeleprompterState({
        text: value,
      });
    }
  };

  const [isPlaying, setIsPlaying] = useState(false);
  const [isSpeedConfig, setIsSpeedConfig] = useState(false);

  const [currentWordIndex, setCurrentWordIndex] = useState(-1);
  const [isAiPromptOpen, setIsAiPromptOpen] = useState(false);
  const [aiPrompt, setAiPrompt] = useState("");
  const [aiTone, setAiTone] = useState<string>("professional");
  const [aiDuration, setAiDuration] = useState<string>("60s");
  
  // Premium feature gating for AI script generation
  const { isPaid } = useFeatureAccess();
  const [showUpgradePrompt, setShowUpgradePrompt] = useState(false);
  
  const words = teleprompterText
    .replace(/\n/g, " ")
    .split(/\s+/)
    .filter((word) => word.length > 0);

  // Use the teleprompter API hook
  const {
    loading: isGeneratingContent,
    error: generationError,
    callApi: generateContent,
  } = useAPI(generateTeleprompterContent);

  const onStart = (_event: DraggableEvent, uiData: DraggableData) => {
    const { clientWidth, clientHeight } = window.document.documentElement;
    const targetRect = draggleRef.current?.getBoundingClientRect();
    if (!targetRect) return;

    // Calculate bounds to keep modal within viewport
    const modalWidth = targetRect.width;
    const modalHeight = targetRect.height;

    // Add some padding from the edges
    const padding = 10;

    setBounds({
      left: -targetRect.left + uiData.x + padding,
      right: clientWidth - (targetRect.left + modalWidth) + uiData.x - padding,
      top: -targetRect.top + uiData.y + padding,
      bottom:
        clientHeight - (targetRect.top + modalHeight) + uiData.y - padding,
    });
  };

  useEffect(() => {
    let timer: NodeJS.Timeout;

    const highlightWords = () => {
      setCurrentWordIndex((prevIndex) => {
        if (prevIndex >= words.length - 1) {
          setIsPlaying(false);
          return -1;
        }
        return prevIndex + 1;
      });
    };

    if (isPlaying) {
      timer = setInterval(highlightWords, Math.max(100, 1000 - highlightSpeed));
    }

    return () => clearInterval(timer);
  }, [isPlaying, highlightSpeed, words.length]);

  const handleReset = () => {
    setIsPlaying(false);
    setCurrentWordIndex(-1);

    setTeleprompterText(defaultTeleprompterText);
    localStorage.setItem("teleprompter_text", defaultTeleprompterText);
    if (onTeleprompterStateChange) {
      onTeleprompterStateChange({
        text: defaultTeleprompterText,
        currentWordIndex: -1,
        isPlaying: false,
        highlightSpeed: highlightSpeed,
      });
    }
  };

  const handleForward = useCallback(() => {
    if (currentWordIndex < words.length - 1) {
      let nextIndex = currentWordIndex + 1;
      // Skip spaces, punctuation, and newlines until we find a real word
      while (
        nextIndex < words.length &&
        /^[\s.,!?;:'"()-]+$/.test(words[nextIndex])
      ) {
        nextIndex++;
      }
      if (nextIndex < words.length) {
        setCurrentWordIndex(nextIndex);
        syncTeleprompterState({
          currentWordIndex: nextIndex,
        });
      }
    }
  }, [currentWordIndex, words]);

  const handleBackward = useCallback(() => {
    if (currentWordIndex >= 1) {
      let prevIndex = currentWordIndex - 1;
      // Skip spaces, punctuation, and newlines until we find a real word
      while (prevIndex >= 0 && /^[\s.,!?;:'"()-]+$/.test(words[prevIndex])) {
        prevIndex--;
      }
      if (prevIndex >= 0) {
        setCurrentWordIndex(prevIndex);
        syncTeleprompterState({
          currentWordIndex: prevIndex,
        });
      }
    }
  }, [currentWordIndex, words]);

  const handleSpeedClose = () => {
    setIsSpeedConfig(false);
  };

  // Common function for state synchronization
  const syncTeleprompterState = useCallback(
    (state: {
      text?: string;
      currentWordIndex?: number;
      isPlaying?: boolean;
      highlightSpeed?: number;
    }) => {
      if (onTeleprompterStateChange) {
        onTeleprompterStateChange({
          text: state.text ?? teleprompterText,
          currentWordIndex: state.currentWordIndex ?? currentWordIndex,
          isPlaying: state.isPlaying ?? isPlaying,
          highlightSpeed: state.highlightSpeed ?? highlightSpeed,
        });
      }
    },
    [
      onTeleprompterStateChange,
      teleprompterText,
      currentWordIndex,
      isPlaying,
      highlightSpeed,
    ]
  );

  const highlightedWordRef = useRef<HTMLSpanElement | null>(null);
  const teleprompterContentRef = useRef<HTMLDivElement | null>(null);

  // Simple scrollIntoView approach with proper centering
  const updateScrollPosition = useCallback(() => {
    if (!highlightedWordRef.current || currentWordIndex < 0) return;

    const activeWord = highlightedWordRef.current;

    // Use scrollIntoView with proper options to center the word
    activeWord.scrollIntoView({
      behavior: "smooth",
      block: "center",
      inline: "nearest", // This prevents horizontal scrolling
    });
  }, [currentWordIndex]);

  useEffect(() => {
    if (currentWordIndex >= 0) {
      // Use requestAnimationFrame for better performance
      requestAnimationFrame(updateScrollPosition);
    }
  }, [currentWordIndex, updateScrollPosition]);

  useEffect(() => {
    if (!open) return;
    
    setTeleprompterText(() => {
      if (currentSceneScript && currentSceneScript.trim()) {
        return currentSceneScript;
      }
      
      if (currentSceneScript === undefined) {
        const storedText = localStorage.getItem("teleprompter_text");
        if (storedText && storedText !== defaultTeleprompterText) {
          return storedText;
        }
      }
      
      return defaultTeleprompterText;
    });
    
    setHighlightSpeed(() => {
      return (
        parseInt(localStorage.getItem("teleprompter_speed") || "500") || 500
      );
    });

    // Initialize bounds when modal opens - will be updated after render
    const { clientWidth, clientHeight } = window.document.documentElement;
    setBounds({
      left: 0,
      top: 0,
      right: clientWidth,
      bottom: clientHeight,
    });
  }, [open, currentSceneScript]);

  // Listen for template script updates from useSceneSync
  useEffect(() => {
    if (!open) return;

    const handleScriptUpdate = (event: CustomEvent) => {
      const { script } = event.detail;
      const newText = script && script.trim() ? script : defaultTeleprompterText;
      if (newText !== teleprompterText) {
        setTeleprompterText(newText);
        syncTeleprompterState({ text: newText });
      }
    };

    window.addEventListener('teleprompter-script-update', handleScriptUpdate as EventListener);

    return () => {
      window.removeEventListener('teleprompter-script-update', handleScriptUpdate as EventListener);
    };
  }, [open, teleprompterText, syncTeleprompterState]);

  // Track previous recording state to detect transitions
  const prevIsRecordingRef = useRef(isRecording);

  // Auto-play teleprompter when recording starts
  useEffect(() => {
    // Only auto-control if teleprompter is open
    if (!open) {
      prevIsRecordingRef.current = isRecording;
      return;
    }

    // Check if we have a valid script (not empty and not default placeholder)
    const hasValidScript = teleprompterText && 
                          teleprompterText.trim() !== '' && 
                          teleprompterText !== defaultTeleprompterText;

    // Only auto-play if recording just started (transition from false to true)
    if (isRecording && !prevIsRecordingRef.current && hasValidScript) {
      // Recording started - auto-play teleprompter
      setIsPlaying(true);
      setCurrentWordIndex(-1); // Start at -1 so timer increments to 0 on first tick
      syncTeleprompterState({
        isPlaying: true,
        currentWordIndex: -1,
      });
    }
    // Only auto-pause if recording just stopped (transition from true to false)
    else if (!isRecording && prevIsRecordingRef.current && isPlaying) {
      // Recording stopped - auto-pause teleprompter
      setIsPlaying(false);
      syncTeleprompterState({
        isPlaying: false,
      });
    }

    // Update ref for next render
    prevIsRecordingRef.current = isRecording;
  }, [isRecording, open, teleprompterText, defaultTeleprompterText, isPlaying, syncTeleprompterState]);

  // Notify parent component of teleprompter state changes
  useEffect(() => {
    syncTeleprompterState({});
  }, [
    teleprompterText,
    currentWordIndex,
    isPlaying,
    highlightSpeed,
    syncTeleprompterState,
  ]);

  const handleAIPrompt = async () => {
    if (!aiPrompt.trim()) return;
    
    // Gate AI generation for premium users only
    if (!isPaid) {
      setShowUpgradePrompt(true);
      return;
    }

    try {
      const result = await generateContent({
        prompt: aiPrompt,
        tone: aiTone,
        duration: aiDuration,
      });

      if (result?.success && result.response) {
        setTeleprompterText(result.response);
        localStorage.setItem("teleprompter_text", result.response);
        setAiPrompt("");
        setIsAiPromptOpen(false);
        syncTeleprompterState({
          text: result.response,
        });
        
        // Call the callback to sync script with current scene
        if (onScriptGenerated) {
          console.log('[Teleprompter] Calling onScriptGenerated callback with script:', {
            scriptLength: result.response.length,
            scriptPreview: result.response.substring(0, 100)
          });
          onScriptGenerated(result.response);
        } else {
          console.warn('[Teleprompter] onScriptGenerated callback not provided');
        }
      }
    } catch (error) {
      console.error("Failed to generate teleprompter content:", error);
    }
  };

  return (
    <Modal
      className={fullpage ? "fullpage-prompter" : ""}
      title={
        fullpage ? null : (
          <div
            className="teleprompter-grip"
            style={{ width: "100%", cursor: "move" }}
          >
            <GripHorizontal color="#fff" />
          </div>
        )
      }
      closable={!fullpage}
      wrapClassName="teleprompter-modal"
      open={open}
      destroyOnHidden
      onCancel={() => {
        // Sync manually edited text to the scene before closing
        if (onScriptGenerated) {
          // Sync if text has changed from the original scene script
          // This includes when user empties the text or edits it
          const hasChanged = teleprompterText !== currentSceneScript;
          const isNotDefaultPlaceholder = teleprompterText !== defaultTeleprompterText;
          const isEmptied = teleprompterText.trim() === '' && currentSceneScript && currentSceneScript.trim() !== '';
          
          if (hasChanged && (isNotDefaultPlaceholder || isEmptied)) {
            console.log('[Teleprompter] Syncing manually edited text on close', {
              textLength: teleprompterText.length,
              isEmpty: teleprompterText.trim() === ''
            });
            onScriptGenerated(teleprompterText);
          }
        }
        onClose();
      }}
      styles={{
        body: {
          padding: isAiPromptOpen ? "0px 24px 120px" : "0px 24px 20px",
        },
      }}
      footer={
        <Flex vertical>
          {isAiPromptOpen && (
            <div className="ai-prompter">
              <Flex vertical gap={8} style={{ width: "100%" }}>
                <Flex gap={8} align="center" style={{ width: "100%" }}>
                  <Input
                    prefix={
                      <Sparkles
                        size={20}
                        color="#fff"
                        style={{ lineHeight: 1 }}
                      />
                    }
                    suffix={
                      <Button
                        variant="solid"
                        className="generate-ai-btn"
                        color={
                          !aiPrompt.trim() || isGeneratingContent
                            ? "geekblue"
                            : "primary"
                        }
                        icon={<ArrowUp />}
                        loading={isGeneratingContent}
                        onClick={handleAIPrompt}
                        disabled={!aiPrompt.trim() || isGeneratingContent}
                      />
                    }
                    placeholder="Write me a script for..."
                    value={aiPrompt}
                    onChange={(e) => setAiPrompt(e.target.value)}
                    onPressEnter={handleAIPrompt}
                    disabled={isGeneratingContent}
                  />
                </Flex>
                <Flex gap={16} align="center" style={{ padding: "0 16px" }}>
                  <Select
                    value={aiTone}
                    onChange={setAiTone}
                    disabled={isGeneratingContent}
                    className="ai-tone-select"
                    style={{ width: "140px" }}
                    options={[
                      { value: "professional", label: "Professional" },
                      { value: "casual", label: "Casual" },
                      { value: "energetic", label: "Energetic" },
                    ]}
                  />
                  <Select
                    value={aiDuration}
                    onChange={setAiDuration}
                    disabled={isGeneratingContent}
                    className="ai-duration-select"
                    style={{ width: "140px" }}
                    options={[
                      { value: "60s", label: "60 seconds" },
                      { value: "90s", label: "90 seconds" },
                      { value: "2min", label: "2 minutes" },
                      { value: "5min", label: "5 minutes" },
                    ]}
                  />
                </Flex>
              </Flex>
              {generationError && (
                <div
                  style={{
                    color: "#ff4d4f",
                    fontSize: "12px",
                    marginTop: "4px",
                  }}
                >
                  {generationError}
                </div>
              )}
            </div>
          )}
          <Flex align="center" justify="space-between" gap={16}>
            <Flex gap={8} align="center" style={{ flex: 1 }}>
              {isSpeedConfig ? (
                <Flex
                  gap={8}
                  align="center"
                  justify="space-between"
                  style={{ flex: 1 }}
                >
                  <Snail size={18} color="#fff" />
                  <Slider
                    style={{ flex: 1 }}
                    min={200}
                    max={800}
                    step={100}
                    tooltip={{ open: false }}
                    value={highlightSpeed}
                    onChange={(value) => {
                      setHighlightSpeed(value);
                      localStorage.setItem(
                        "teleprompter_speed",
                        value.toString()
                      );
                      syncTeleprompterState({
                        highlightSpeed: value,
                      });
                    }}
                  />
                  <Rabbit size={18} color="#fff" />
                  <Tooltip title="Close speed settings">
                    <Button
                      type="link"
                      icon={<X size={18} color="#fff" />}
                      onClick={handleSpeedClose}
                    />
                  </Tooltip>
                </Flex>
              ) : (
                <Tooltip title="Reading speed">
                  <Button
                    type="link"
                    icon={<Timer color="#fff" size={18} />}
                    onClick={() => {
                      setIsSpeedConfig(true);
                    }}
                  ></Button>
                </Tooltip>
              )}

              {!isPlaying &&
                currentWordIndex === -1 &&
                !isSpeedConfig &&
                !fullpage && (
                  <Tooltip title="AI script generator">
                    <Button
                      type={isAiPromptOpen ? "primary" : "link"}
                      icon={<Sparkles color="#fff" size={18} />}
                      onClick={() => {
                        setIsAiPromptOpen(!isAiPromptOpen);
                      }}
                    ></Button>
                  </Tooltip>
                )}
            </Flex>

            <Flex gap={8} align="center" justify="center">
              <Flex gap={4}>
                <Tooltip title="Previous sentence">
                  <Button
                    type="link"
                    icon={<StepBack color="#fff" size={18} />}
                    onClick={handleBackward}
                  ></Button>
                </Tooltip>
                {isPlaying ? (
                  <Tooltip title="Pause">
                    <Button
                      variant="outlined"
                      color="default"
                      icon={<PauseIcon style={{ lineHeight: 18 }} size={18} />}
                      onClick={() => {
                        setIsPlaying(false);
                        syncTeleprompterState({
                          isPlaying: false,
                        });
                      }}
                    ></Button>
                  </Tooltip>
                ) : (
                  <Tooltip title="Play">
                    <Button
                      variant="outlined"
                      color="default"
                      icon={<PlayIcon style={{ lineHeight: 18 }} size={18} />}
                      onClick={() => {
                        setIsPlaying(true);
                        setIsSpeedConfig(false);
                        setIsAiPromptOpen(false);
                        if (currentWordIndex === -1) {
                          setCurrentWordIndex(0);
                        }
                        syncTeleprompterState({
                          isPlaying: true,
                          currentWordIndex: currentWordIndex === -1 ? 0 : currentWordIndex,
                        });
                      }}
                    ></Button>
                  </Tooltip>
                )}
                <Tooltip title="Next sentence">
                  <Button
                    type="link"
                    icon={<StepForward color="#fff" size={18} />}
                    onClick={handleForward}
                  ></Button>
                </Tooltip>
              </Flex>
            </Flex>
            <Flex gap={8} justify="end" style={{ flex: 1 }}>
              <Tooltip title="Reset to beginning">
                <Button
                  type="link"
                  icon={<RotateCcw color="#fff" size={18} />}
                  onClick={handleReset}
                ></Button>
              </Tooltip>
            </Flex>
          </Flex>
        </Flex>
      }
      centered
      mask={false}
      keyboard={false}
      maskClosable={false}
      modalRender={(modal) =>
        fullpage ? (
          modal
        ) : (
          <Draggable
            disabled={false}
            bounds={bounds}
            nodeRef={draggleRef}
            onStart={onStart}
            handle=".teleprompter-grip"
            defaultPosition={{ x: 0, y: 0 }}
            scale={1}
          >
            <div ref={draggleRef}>{modal}</div>
          </Draggable>
        )
      }
      zIndex={1005}
    >
      <div
        ref={teleprompterContentRef}
        className="teleprompter-content"
        style={{
          position: "relative",
          maxHeight: "60vh",
          overflow: "auto",
        }}
      >
        {!isPlaying && currentWordIndex === -1 ? (
          <Paragraph
            editable={{
              tooltip: "click to edit text",
              onChange: (value) => {
                validateAndSetTeleprompterText(value);
              },
              triggerType: ["text"],
            }}
            style={{ whiteSpace: "pre-wrap" }}
          >
            {teleprompterText}
          </Paragraph>
        ) : (
          <Paragraph style={{ whiteSpace: "pre-wrap" }}>
            {words.map((word, index) => {
              // Calculate distance from current word for smooth gradient effect
              const distance = Math.abs(index - currentWordIndex);

              // render actual words with enhanced smooth highlight animation
              return (
                <span
                  key={index}
                  ref={index === currentWordIndex ? highlightedWordRef : null}
                  onClick={() => {
                    setCurrentWordIndex(index);
                    syncTeleprompterState({
                      currentWordIndex: index,
                    });
                  }}
                  className={`teleprompter-word ${
                    index === currentWordIndex
                      ? "teleprompter-word--active"
                      : ""
                  } ${
                    index === currentWordIndex - 1
                      ? "teleprompter-word--previous"
                      : ""
                  } ${
                    index === currentWordIndex + 1
                      ? "teleprompter-word--next"
                      : ""
                  } ${
                    distance <= 3 && distance > 1
                      ? "teleprompter-word--near"
                      : ""
                  } ${distance > 5 ? "teleprompter-word--far" : ""}`}
                  style={{
                    cursor: "pointer",
                    marginInlineEnd: 0,
                    transitionDelay: `${Math.min(distance * 20, 100)}ms`,
                  }}
                >
                  {word}
                </span>
              );
            })}
          </Paragraph>
        )}
      </div>
      
      <UpgradePrompt
        visible={showUpgradePrompt}
        onClose={() => setShowUpgradePrompt(false)}
        feature="AI Script Generation"
        description="Upgrade to Pro to unlock AI-powered script generation. Create professional teleprompter scripts in seconds with the help of artificial intelligence."
      />
    </Modal>
  );
};

export default TeleprompterModal;
